/**
 * DaddyTalkLauncher — Sticky floating "Chat with a recruiter" launcher.
 *
 * V1.6.1 — Opens an in-place "Who's Online" drawer instead of
 * navigating to the marketing page. Users clicking the launcher
 * have already expressed intent; they want to see availability
 * RIGHT NOW, not read the pitch.
 *
 * Hides on `/daddytalk/*` and superadmin routes so it doesn't
 * self-overlap or spam the internal team.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChatCircleText } from "@phosphor-icons/react";
import DaddyTalkAvailabilityDrawer from "./DaddyTalkAvailabilityDrawer";
import { useAuth } from "@/context/AuthContext";
import useDaddyTalkHeartbeat from "@/hooks/useDaddyTalkHeartbeat";

const HIDE_ON_PATHS = [
    "/daddytalk",
    "/superadmin",
    "/interview",
    "/apply",
    "/quick-apply",
];

// V1.6.4 — The launcher is a CANDIDATE-facing shortcut into DaddyTalk.
// Recruiters, admins, HMs, and superadmins already have `/messages`
// (their own inbox) — showing them a "Chat with a recruiter" pill on
// their own dashboard is confusing (they'd end up messaging peers).
const STAFF_ROLES = new Set(["recruiter", "admin", "hiring_manager", "superadmin"]);

export default function DaddyTalkLauncher() {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const [pulse, setPulse] = useState(true);
    const [open, setOpen] = useState(false);

    // V1.6.2 — recruiter presence heartbeat. Self-gates by role so
    // anon candidates + candidates never fire the beacon.
    useDaddyTalkHeartbeat(user);

    useEffect(() => {
        const t = setTimeout(() => setPulse(false), 3000);
        return () => clearTimeout(t);
    }, []);

    if (HIDE_ON_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
        return null;
    }
    // V1.6.4 — Hide from staff. They use /messages, not the launcher.
    // V1.7.1 — Don't render at all until AuthContext resolves. `null`
    // means still fetching `/auth/me`; showing the launcher and then
    // yanking it away when we learn the user is staff causes an
    // annoying flicker on landing/dashboard pages.
    if (user === null) {
        return null;
    }
    if (user && STAFF_ROLES.has(user.role)) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                data-testid="daddytalk-launcher"
                onClick={() => setOpen(true)}
                aria-label="Chat with a recruiter on DaddyTalk"
                className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[80] group focus:outline-none flex items-center gap-2 focus:ring-2 focus:ring-cobalt/40"
            >
                {/* Persistent label pill — visible on ALL viewports so mobile
                    users don't need to hover to discover the feature. */}
                <span
                    data-testid="daddytalk-launcher-label"
                    className="bg-ink text-white text-xs sm:text-sm font-black uppercase tracking-wider px-3 py-2 rounded-full shadow-lg whitespace-nowrap inline-flex items-center gap-1.5 group-hover:bg-black transition-colors"
                >
                    <ChatCircleText size={12} weight="bold" />
                    <span className="hidden xs:inline sm:inline">Chat with a recruiter</span>
                    <span className="xs:hidden sm:hidden">Chat</span>
                </span>

                {/* Uncle D avatar */}
                <span className="relative shrink-0">
                    {pulse && (
                        <span className="absolute inset-0 rounded-full bg-cobalt/40 animate-ping" />
                    )}
                    <span className="relative block w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border-4 border-white shadow-2xl bg-[#F0F6FF] transition-transform group-hover:scale-105 group-active:scale-95">
                        <img
                            src="/mascot/daddytalk/avatar_hello.png"
                            alt=""
                            className="w-full h-full object-cover pointer-events-none"
                            draggable={false}
                        />
                    </span>
                    <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" />
                </span>
            </button>

            <DaddyTalkAvailabilityDrawer open={open} onOpenChange={setOpen} />
        </>
    );
}
